import {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import * as React from 'react';
import {
  ResourceList,
  ResourceItem,
  ResourceItemProps,
  Thumbnail,
  Pagination,
  BlockStack,
  Divider,
  Tabs,
  TabProps,
} from '@shopify/polaris';
import {cloneDeep, snakeCase} from 'lodash';
import {useDebounce} from 'use-debounce';
import {ImageIcon} from '@shopify/polaris-icons';
import {TranslatableResourceType} from 'types';
import {useTranslatableResource} from 'features/Translations/TranslatableResource/hooks';
import {useI18n} from '@shopify/react-i18n';
import {useApolloClient} from '@shopify/react-graphql';
import {SearchResource} from 'types/graphql/default-types';
import {
  nodesFromEdges,
  parseGidType,
} from '@shopify/admin-graphql-api-utilities';
import {useLocalStorage} from '@web-utilities/local-storage';
import {THEME_ID_STORAGE_KEY} from 'features/Translations/constants';
import {useNavigate} from 'react-router-dom';
import {useUrlParams} from 'utilities/use-url-params';
import {getSearchFromQuery} from 'utilities/url';

import {NoResults} from '../NoResults';
import {
  createResourceListItems,
  isUnsearchableThemeResourceType,
  isResourceTypesWithNoImage,
  isResourceTypeWithoutSidebarPagination,
  isSearchableThemeResourceType,
  buildQueryParams,
} from '../../utils';
import {PAGE_SIZE} from '../../constants';

import NavigationQuery, {
  NavigationQueryData,
} from './graphql/NavigationQuery.graphql';
import styles from './Navigation.scss';
import {NavigationSkeleton} from './components';

const scope = 'Navigation';

export interface Props {
  loading: boolean;
  hidden?: boolean;
  onFetchCompleted?: (itemsCount: number) => void;
}

export function Navigation({loading, hidden = false, onFetchCompleted}: Props) {
  const [i18n] = useI18n();
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const {urlParams} = useUrlParams();
  const {
    resourceId,
    resourceType,
    resourceDisplayTitle,
    resourceDisplayImage,
    translatableEntries,
    translatableResourcesQueryLoading,
  } = useTranslatableResource();
  const [navLoading, setNavLoading] = useState(false);
  const [hasNoResult, setHasNoResult] = useState(false);
  const [pagination, setPagination] = useState<{
    direction: 'next' | 'previous';
    cursor?: string;
  }>({
    direction: 'next',
    cursor: undefined,
  });
  const [userQuery, setUserQuery] = useState('');
  const [debouncedUserQuery] = useDebounce(userQuery, 500);
  const [statusFilter, setStatusFilter] = useState<{
    key: string;
    value: string;
  } | null>(null);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor?: string | null;
    endCursor?: string | null;
  }>({
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null,
    endCursor: null,
  });
  const [searchResults, setSearchResults] = useState<
    NavigationQueryData.ShopSearchEdgesNode[]
  >([]);
  const [selected, setSelected] = useState<string[]>([]);
  const onFetchCompletedRef = useRef(onFetchCompleted);
  onFetchCompletedRef.current = onFetchCompleted;

  const themeItem = urlParams.item || '';
  const [{value: storedThemeId}] =
    useLocalStorage<string>(THEME_ID_STORAGE_KEY);
  const themeId = storedThemeId || '';
  const isUnsearchableThemeResource = useMemo(
    () => isUnsearchableThemeResourceType(resourceType),
    [resourceType],
  );
  const isSearchableThemeResource = useMemo(
    () => isSearchableThemeResourceType(resourceType),
    [resourceType],
  );

  const resourceListItems = useMemo(() => {
    if (resourceType === TranslatableResourceType.Shop) {
      if (!resourceId) return [];
      return [
        {
          id: resourceId,
          name: i18n.translate('Navigation.static.metaContent'),
        },
      ];
    }

    return createResourceListItems(
      resourceType,
      translatableEntries,
      searchResults,
      i18n,
    );
  }, [resourceType, searchResults, translatableEntries, i18n, resourceId]);

  const query = useMemo(() => {
    const parts = [];

    if (debouncedUserQuery) {
      parts.push(debouncedUserQuery);
    }

    if (statusFilter) {
      parts.push(`${statusFilter.key}:${statusFilter.value}`);
    }

    return parts.join(' AND ');
  }, [debouncedUserQuery, statusFilter]);

  const fetchSidebarItems = useCallback(async () => {
    if (
      isUnsearchableThemeResource ||
      resourceType === TranslatableResourceType.Shop
    )
      return;

    let parentResourceId = '';
    if (isSearchableThemeResource && themeId) {
      parentResourceId = `gid://shopify/OnlineStoreTheme/${themeId}`;
    }

    setNavLoading(true);
    setSelected([]);
    const {data} = await apollo.query<
      NavigationQueryData,
      NavigationQueryData.Variables
    >({
      query: NavigationQuery,
      variables: {
        first: pagination.direction === 'next' ? PAGE_SIZE : undefined,
        last: pagination.direction === 'previous' ? PAGE_SIZE : undefined,
        query,
        type: resourceType as unknown as SearchResource,
        after: pagination.direction === 'next' ? pagination.cursor : undefined,
        before:
          pagination.direction === 'previous' ? pagination.cursor : undefined,
        parentResourceId,
      },
    });

    const searchResults = cloneDeep(data.shop.search);

    if (
      resourceId &&
      !searchResults.edges.map((edge) => edge.node.id).includes(resourceId)
    ) {
      searchResults.edges.unshift({
        __typename: 'SearchResultEdge',
        node: {
          __typename: 'SearchResult',
          id: resourceId,
          title: resourceDisplayTitle,
          ...(resourceDisplayImage && {
            image: {
              __typename: 'SearchResultImage',
              url: resourceDisplayImage.url,
            },
          }),
        },
      });
    }

    if (!resourceId && searchResults.edges.length) {
      const searchId = searchResults.edges[0].node.id;
      if (
        [
          TranslatableResourceType.DeliveryProfile,
          TranslatableResourceType.PackingSlipTemplate,
        ].includes(resourceType)
      ) {
        const searchResourceType = parseGidType(
          searchId,
        ) as TranslatableResourceType;
        const resourceTypeForUrl = snakeCase(searchResourceType).toLowerCase();
        navigate(
          {
            pathname: `/localize/${resourceTypeForUrl}`,
            search: getSearchFromQuery(
              buildQueryParams({
                ...urlParams,
                id: searchId,
                resourceType: searchResourceType,
              }),
            ),
          },
          {
            replace: true,
          },
        );
      } else {
        navigate(
          {
            search: getSearchFromQuery(
              buildQueryParams({
                ...urlParams,
                id: searchId,
                resourceType,
              }),
            ),
          },
          {replace: true},
        );
      }
    }

    const results = nodesFromEdges(searchResults.edges);
    setSearchResults(results);
    setPageInfo(searchResults.pageInfo);

    setNavLoading(false);
    onFetchCompletedRef.current?.(results.length);
    setHasNoResult(results.length === 0);
  }, [
    isUnsearchableThemeResource,
    isSearchableThemeResource,
    themeId,
    apollo,
    pagination.direction,
    pagination.cursor,
    query,
    resourceType,
    resourceId,
    resourceDisplayTitle,
    resourceDisplayImage,
    navigate,
    urlParams,
  ]);

  const resetUserQuery = useCallback(() => setUserQuery(''), []);
  const resetStatusFilter = useCallback(() => setStatusFilter(null), []);
  const resetPagination = useCallback(
    () => setPagination({direction: 'next', cursor: undefined}),
    [],
  );
  const resetPageInfo = useCallback(
    () =>
      setPageInfo({
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
      }),
    [],
  );

  const resetFilters = useCallback(() => {
    resetUserQuery();
    resetStatusFilter();
    resetPagination();
  }, [resetPagination, resetStatusFilter, resetUserQuery]);

  useEffect(() => {
    // Fetch sidebar items on mount & when one of the dependencies changes
    fetchSidebarItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, pagination]);

  /**
   * Push the selected item title & image when they are ready
   */
  useEffect(() => {
    setSearchResults((previousResults) => {
      if (
        !resourceId ||
        !previousResults.length ||
        previousResults[0].id !== resourceId
      )
        return previousResults;

      return [
        {
          ...previousResults[0],
          ...(resourceDisplayTitle && {title: resourceDisplayTitle}),
          ...(resourceDisplayImage && {
            image: {
              __typename: 'SearchResultImage',
              url: resourceDisplayImage.url,
            },
          }),
        },
        ...previousResults.slice(1),
      ];
    });
  }, [resourceDisplayTitle, resourceDisplayImage, resourceId]);

  useEffect(() => {
    // Reset pagination when user query changes
    resetPagination();
  }, [debouncedUserQuery, resetPagination]);

  const handleOnSelectionChange = useCallback(
    async (selectedItemId: string) => {
      if (!selectedItemId || !resourceId) return;

      const query =
        isUnsearchableThemeResource ||
        resourceType === TranslatableResourceType.CookieBanner
          ? buildQueryParams({
              ...urlParams,
              id: resourceId,
              resourceType,
              item: selectedItemId,
            })
          : buildQueryParams({
              ...urlParams,
              id: selectedItemId,
              resourceType,
            });

      if (
        [
          TranslatableResourceType.DeliveryProfile,
          TranslatableResourceType.PackingSlipTemplate,
        ].includes(resourceType)
      ) {
        const clickedResourceType = parseGidType(
          selectedItemId,
        ) as TranslatableResourceType;
        const resourceTypeForUrl = snakeCase(clickedResourceType).toLowerCase();
        navigate(
          {
            pathname: `/localize/${resourceTypeForUrl}`,
            search: getSearchFromQuery(query),
          },
          {
            replace: true,
          },
        );
        return;
      }
      navigate(
        {
          search: getSearchFromQuery(query),
        },
        {replace: true},
      );
    },
    [
      isUnsearchableThemeResource,
      navigate,
      resourceId,
      resourceType,
      urlParams,
    ],
  );

  // Select the correct sidebar item whenever translatableResourcesQueryLoading is finished
  useEffect(() => {
    if (
      translatableResourcesQueryLoading ||
      navLoading ||
      isUnsearchableThemeResource ||
      !resourceId
    )
      return;

    const selection =
      resourceType === TranslatableResourceType.CookieBanner
        ? urlParams.item || ''
        : resourceId;

    if (selected && selection !== selected[0]) {
      setSelected([selection]);
    }
  }, [
    resourceId,
    urlParams.item,
    resourceType,
    selected,
    translatableResourcesQueryLoading,
    isUnsearchableThemeResource,
    navLoading,
  ]);

  useEffect(() => {
    if (translatableResourcesQueryLoading || !isUnsearchableThemeResource)
      return;
    if (
      themeItem &&
      resourceListItems.some(({id}) => id.startsWith(themeItem)) &&
      selected[0] !== themeItem
    ) {
      setSelected([themeItem]);
    } else if (resourceListItems.length && !selected.length) {
      handleOnSelectionChange(resourceListItems[0].id);
    }
  }, [
    handleOnSelectionChange,
    resourceListItems,
    resourceType,
    themeItem,
    translatableResourcesQueryLoading,
    selected,
    isUnsearchableThemeResource,
  ]);

  /**
   * Reset states when the resource type changes
   */
  useEffect(() => {
    resetFilters();
    resetPageInfo();
    setSearchResults([]);
    setSelected([]);
    setHasNoResult(false);
  }, [resourceType, resetFilters, resetPageInfo]);

  const renderItem = ({
    id,
    name,
    url,
  }: {
    id: string;
    name: string;
    url?: string;
    onClick?: string;
  }): React.ReactNode => {
    const props: ResourceItemProps = {
      id,
      url,
      onClick: () => {},
    };
    const correspondingSearchResultNode = searchResults?.find(
      ({id: nodeId}) => nodeId === id,
    );

    const thumbnailMarkup = isResourceTypesWithNoImage(
      resourceType,
    ) ? undefined : (
      <Thumbnail
        source={correspondingSearchResultNode?.image?.url || ImageIcon}
        alt={name}
        size="extraSmall"
      />
    );

    return (
      <ResourceItem
        {...props}
        media={thumbnailMarkup}
        verticalAlignment="center"
        key={id}
      >
        <span className={styles.ItemName}>{name}</span>
      </ResourceItem>
    );
  };

  const resourceStatuses = (() => {
    if (resourceType === TranslatableResourceType.Product) {
      return {
        key: 'status',
        label: i18n.translate('filters.resourceStatus', {scope}),
        choices: ['active', 'draft', 'archived'].map((status) => ({
          value: status,
          label: i18n.translate(`filters.${status}`, {scope}),
        })),
      };
    }

    if (
      [
        TranslatableResourceType.OnlineStoreArticle,
        TranslatableResourceType.OnlineStorePage,
      ].includes(resourceType)
    ) {
      return {
        key: 'published_status',
        label: i18n.translate('filters.resourceStatus', {scope}),
        choices: ['visible', 'hidden'].map((status) => ({
          value: status,
          label: i18n.translate(`filters.${status}`, {scope}),
        })),
      };
    }
  })();

  const resourceStatusFilter = (() => {
    if (!resourceStatuses) return null;

    const {key, choices} = resourceStatuses;

    const tabs: TabProps[] = [
      {
        id: 'all',
        content: i18n.translate('filters.all', {scope}),
        accessibilityLabel: i18n.translate('filters.all', {scope}),
      },
      ...choices.map((choice) => ({
        id: choice.value,
        content: choice.label,
        accessibilityLabel: choice.label,
      })),
    ];

    return (
      <>
        <Tabs
          tabs={tabs}
          onSelect={(selectedTabIndex) => {
            const selectedTab = tabs[selectedTabIndex];
            if (selectedTab.id === 'all') {
              setStatusFilter(null);
            } else {
              setStatusFilter({
                key,
                value: selectedTab.id,
              });
            }
          }}
          selected={
            statusFilter
              ? tabs.findIndex(({id}) => id === statusFilter.value)
              : 0
          }
        />
        <Divider />
      </>
    );
  })();

  const onPrevious = useCallback(() => {
    if (pageInfo.startCursor) {
      setPagination({
        direction: 'previous',
        cursor: pageInfo.startCursor,
      });
    }
  }, [pageInfo.startCursor]);

  const onNext = useCallback(() => {
    if (pageInfo.endCursor) {
      setPagination({
        direction: 'next',
        cursor: pageInfo.endCursor,
      });
    }
  }, [pageInfo.endCursor]);

  const paginationControl = useMemo(
    () =>
      isResourceTypeWithoutSidebarPagination(resourceType) ||
      (!pageInfo.hasPreviousPage && !pageInfo.hasNextPage) ? null : (
        <div className={styles.PaginationWrapper}>
          <BlockStack inlineAlign="center">
            <Pagination
              hasPrevious={!navLoading && pageInfo.hasPreviousPage}
              onPrevious={onPrevious}
              hasNext={!navLoading && pageInfo.hasNextPage}
              onNext={onNext}
            />
          </BlockStack>
        </div>
      ),
    [
      navLoading,
      onNext,
      onPrevious,
      pageInfo.hasNextPage,
      pageInfo.hasPreviousPage,
      resourceType,
    ],
  );

  const emptyStateMarkup = !navLoading &&
    !translatableResourcesQueryLoading &&
    !resourceListItems.length && <NoResults />;

  if (hidden) {
    return null;
  }

  if (!hasNoResult && loading) {
    return <NavigationSkeleton withFilter={Boolean(resourceStatusFilter)} />;
  }

  return (
    <div className={styles.NavigationWrapper}>
      {resourceStatusFilter}
      <ResourceList
        loading={navLoading || translatableResourcesQueryLoading}
        items={resourceListItems}
        renderItem={renderItem}
        selectedItems={selected}
        onSelectionChange={(selectedItems) =>
          handleOnSelectionChange(selectedItems[1])
        }
        emptyState={emptyStateMarkup}
      />
      {paginationControl}
    </div>
  );
}
